import React, { useMemo, useState } from 'react';
import { Tabs as AntTabs } from 'antd';
import { useSelector } from 'react-redux';
import { collect } from 'collect.js';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useValidator } from '../../../hooks/utility/useValidator';
import { useForceUpdate } from '../../../hooks/utility/useForceUpdate';
import ConfirmCancelModal from '../../../modals/ConfirmCancelModal';
import GeneralTab from '../../../components/takeout/GeneralTab';
import PickupTab from '../../../components/takeout/PickupTab';
import DeliveryTab from '../../../components/takeout/DeliveryTab';
import useFeedbackLabel from '../../../hooks/ui/useFeedbackLabel';
import useApiClient from '../../../hooks/api/useApiClient';
import RoomServiceTab from '../../../components/takeout/RoomServiceTab';
import {
  CompanyOperationalStatus,
  DeliveryMode,
  MIN_EXTERNAL_DELIVERY_SCHEDULE_AHEAD_MINUTES,
  ORDER_METHODS,
} from '../../../services/exports/Constants';
import LiveOrdersDrawer from '../../../modals/live-orders/LiveOrdersDrawer';
import LicensePaywallModal from '../../../components/provider-billing/LicensePaywallModal';
import useLicenseGate from '../../../hooks/provider-billing/useLicenseGate';
import {
  LicenseErrorCode,
  LicenseProductKey,
  PARTNER_PLAN_LICENSE_PAYLOAD,
  holdsLicense,
} from '../../../services/tenant/providerBilling';
import useHelpers from '../../../hooks/context/useHelpers';
import useServiceProvider from '../../../hooks/data/useServiceProvider';
import { PaywallGate, paywallBlocker } from '../../../services/analytics/paywallEvents';
import {
  processingFeeBlocksRestaurant,
  setsStripeDestinationRates,
} from '../../../services/helpers/StripeProcessingFees';
import ProcessingFeeRateMissingModal from '../../../modals/processing-fees/ProcessingFeeRateMissingModal';
import usePermissions, { ManagerPermission } from '../../../hooks/auth/usePermissions';
import useHistory from '../../../hooks/navigation/useHistory';
import useLinkedTab from '../../../hooks/navigation/useLinkedTab';
import { ScreenEnum } from '../../../hooks/navigation/Screens';

export const Tabs = {
  General: 'general',
  Pickup: 'pickup',
  Delivery: 'delivery',
  RoomService: 'room_service',
};

export default function TakeoutView() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:OrderSettings:Takeout' });

  const { company } = useSelector((state) => state.currentCompany);
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  const { can, isSuperAdmin } = usePermissions();
  const history = useHistory();

  const { CompaniesManager } = useApiClient();
  const validator = useValidator();
  const forceUpdate = useForceUpdate();
  const { getActiveTab, setActiveTab } = useHelpers();
  // Room/Table service is not offered to self-serve (PLG) partners (decided 2026-09-04).
  const { hasPlgOnboarding } = useServiceProvider();
  const hiddenTabs = hasPlgOnboarding ? [Tabs.RoomService] : [];

  const persistedTab = getActiveTab('takeout_settings', Tabs.General);
  const activeTab = hiddenTabs.includes(persistedTab) ? Tabs.General : persistedTab;

  // A link can open this view on a tab (`?tab=delivery`): the partner onboarding wizard's "Advanced zone setup"
  // escape hatch, and the platform agent's Open links (QL22, src/navigation/screenTabs.js).
  useLinkedTab(ScreenEnum.OrderSettingsTakeout, { hidden: hiddenTabs });

  const [data, setData] = useState({
    ...company,
    hidden_order_methods: company?.hidden_order_methods ?? [],
    has_scheduled_orders_time_slot_order_limit: company?.scheduled_orders_time_slot_order_limit !== null,
    average_order_preparation_time_min: company?.average_order_preparation_time_min ?? 20,
    average_order_delivery_time_min: company?.average_order_delivery_time_min ?? 45,
    delivery_providers: company?.delivery_providers ?? [],
    pickup_time_optimization_strategies: company?.pickup_time_optimization_strategies ?? {},
  });

  const setField = (field, value) =>
    setData((current) => {
      const next = { ...current, [field]: value };

      // Switching to external delivery forces the delivery order-ahead window up to the
      // provider minimum, mirroring the backend overwrite on delivery-mode change.
      if (
        field === 'delivery_mode' &&
        value === DeliveryMode.External &&
        Number(current.min_schedule_ahead_time_delivery ?? 0) < MIN_EXTERNAL_DELIVERY_SCHEDULE_AHEAD_MINUTES
      ) {
        next.min_schedule_ahead_time_delivery = MIN_EXTERNAL_DELIVERY_SCHEDULE_AHEAD_MINUTES;
      }

      return next;
    });
  const toggleOrderMethodVisible = (orderMethod) =>
    setField(
      'hidden_order_methods',
      data.hidden_order_methods.includes(orderMethod)
        ? collect(data.hidden_order_methods)
            .reject((item) => item === orderMethod)
            .toArray()
        : [...(data.hidden_order_methods ?? []), orderMethod],
    );

  const [loading, setLoading] = useState(false);
  const [showUpdateConfirmationModal, setShowUpdateConfirmationModal] = useState(false);
  const [showConfirmLiveOrdersSwitchDrawer, setShowConfirmLiveOrdersSwitchDrawer] = useState(false);

  // The licence paywall (PROD-494). Going live is a value moment, so a partner
  // on platform billing confirms the Partner Plan before the restaurant can
  // take orders. The purchase rides along with the launch request, so the two
  // commit together on the backend.
  // The launch is refused without a payment method even when the plan is
  // already held, so the confirmation asks for one too.
  const gate = useLicenseGate(company?.id, LicenseProductKey.PartnerPlan, {
    company,
    paymentMethodToConfirm: true,
    paywallGate: PaywallGate.LiveOrders,
  });
  const { reportPaywallSubscribed, close: closePaywall } = gate;

  // The purchase rides along only when there is one to make: a restaurant
  // that already holds the plan confirms without a terms box, so sending
  // `terms_accepted` for it would record an acceptance that never happened.
  const buying = gate.paywallActive && !holdsLicense(gate.entitlements, LicenseProductKey.PartnerPlan);

  // Go Live always confirms in the modal for a billing partner (the
  // already-licensed variant when the plan is held); everyone else goes
  // straight to the drawer, exactly as before.
  function launch() {
    gate.gate(() => setShowConfirmLiveOrdersSwitchDrawer(true), { always: true });
  }

  // A destination partner's restaurant cannot launch before the partner sets
  // its Stripe processing fee or consents to a 0% margin (the backend refuses
  // it too), so that is said before the licence gate. A super admin goes on to
  // the backend refusal, whose failed-check list is where they can waive it.
  const [blockedOnFeeRate, setBlockedOnFeeRate] = useState(false);

  const restaurantProvider = company?.service_provider ?? serviceProvider;

  function requestGoLive() {
    if (!isSuperAdmin && processingFeeBlocksRestaurant(company, restaurantProvider)) {
      setBlockedOnFeeRate(true);

      return;
    }

    launch();
  }

  // Only someone who sets the partner's rates is sent to them; the
  // restaurant's own users are told to ask the partner.
  const canSetFeeRate =
    blockedOnFeeRate &&
    setsStripeDestinationRates(restaurantProvider) &&
    can(ManagerPermission.WriteProcessingFeeRates);

  async function subscribeAndGoLive() {
    // Already licensed: there is nothing to buy, so the confirmation hands
    // over to the drawer — the same launch review every other tenant gets,
    // including the checks it may skip. Firing a bare launch here would only
    // earn a refusal that opens the drawer anyway.
    if (!buying) {
      gate.close();
      setShowConfirmLiveOrdersSwitchDrawer(true);

      return { ok: true, dismissed: true };
    }

    const {
      data: response,
      success,
      status,
    } = await CompaniesManager.updateOperationalStatus({
      operational_status: CompanyOperationalStatus.Live,
      skipped_checks: [],
      license: PARTNER_PLAN_LICENSE_PAYLOAD,
    });

    if (success) {
      setField('operational_status', response.operational_status);
      gate.refresh();
      reportPaywallSubscribed();

      return { ok: true };
    }

    // A refused launch is the normal first outcome: the drawer is where the
    // partner reviews and skips what it can. The purchase travels with it, so
    // nothing is bought until a launch actually succeeds — and the paywall's
    // outcome is that launch's (see the drawer below), unless the refusal was the
    // plan or the card itself.
    if (status === 400 && response?.error_code === LicenseErrorCode.OperationalStatusCheckFailed) {
      const failedChecks = Object.keys(response?.data ?? {});
      gate.reportPaywallRefusal({ failedChecks });

      if (paywallBlocker({ failedChecks })) {
        gate.close();
      } else {
        gate.handOffPaywall();
      }

      setShowConfirmLiveOrdersSwitchDrawer(true);

      return { ok: true, dismissed: true };
    }

    gate.reportPaywallRefusal({ error: response });

    return { ok: false, error: response };
  }

  const { feedback, setFeedback, resetFeedback, renderFeedbackLabel } = useFeedbackLabel({ className: 'mt-md' });

  function fieldsChangedRequireConfirmation() {
    return !!collect([
      'has_pickup',
      'has_delivery',
      'has_room_service',
      'delivery_enabled',
      'scheduled_orders_time_slot_interval',
      'scheduled_orders_time_slot_order_limit',
      'order_confirmation_time_extra_minutes',
      'average_order_preparation_time_min',
      'average_order_preparation_time_max',
      'average_order_delivery_time_min',
      'average_order_delivery_time_max',
      'min_schedule_ahead_time_pickup',
      'last_order_gap_pickup',
      'min_schedule_ahead_time_delivery',
      'last_order_gap_delivery',
    ]).first((item) => data[item] !== company[item]);
  }

  const save = async (requiresConfirmation = true) => {
    if (!validator.allValid()) {
      collect(validator.errorMessages).each((message) => toast.error(message));
      validator.showMessages();

      return forceUpdate();
    }

    if (requiresConfirmation && fieldsChangedRequireConfirmation()) {
      return setShowUpdateConfirmationModal(true);
    }

    setLoading(true);
    const { success, data: response } = await CompaniesManager.updateTakeoutSettings(data);
    setLoading(false);
    setShowUpdateConfirmationModal(false);

    if (!success) {
      toast.error(t('toasts.failed_to_save_changes'));

      return setFeedback({
        type: 'error',
        message: response?.message,
        errors: response?.errors,
      });
    }

    resetFeedback();

    return toast.success(t('toasts.changes_got_saved'));
  };

  const renderUpdateConfirmationModal = useMemo(
    () =>
      showUpdateConfirmationModal && (
        <ConfirmCancelModal
          show={showUpdateConfirmationModal}
          toggleModal={() => setShowUpdateConfirmationModal(!showUpdateConfirmationModal)}
          title={t('modals.update_confirmation.title')}
          primaryButtonTitle={t('modals.update_confirmation.buttons.confirm')}
          secondaryButtonTitle={t('modals.update_confirmation.buttons.cancel')}
          onConfirm={() => save(false)}
          loading={loading}
        />
      ),
    [showUpdateConfirmationModal, loading],
  );

  const renderConfirmLiveOrdersSwitchDrawer = useMemo(
    () => (
      <LiveOrdersDrawer
        key={showConfirmLiveOrdersSwitchDrawer ? 'open' : 'closed'}
        show={showConfirmLiveOrdersSwitchDrawer}
        toggleDrawer={() => {
          // Closing the review ends a paywall handed off to it, bought or not.
          if (showConfirmLiveOrdersSwitchDrawer) {
            closePaywall();
          }

          setShowConfirmLiveOrdersSwitchDrawer(!showConfirmLiveOrdersSwitchDrawer);
        }}
        company={data}
        onChange={(field, value) => {
          setField(field, value);

          // The launch the purchase travelled with went through.
          if (field === 'operational_status' && value === CompanyOperationalStatus.Live) {
            reportPaywallSubscribed();
          }
        }}
        license={buying ? PARTNER_PLAN_LICENSE_PAYLOAD : null}
      />
    ),
    [showConfirmLiveOrdersSwitchDrawer, data?.operational_status, buying, closePaywall, reportPaywallSubscribed],
  );

  return (
    <>
      <LicensePaywallModal
        {...gate.modalProps}
        onSubscribe={subscribeAndGoLive}
        successTitle={t('paywall.live_title', { name: company?.name })}
      />
      <ProcessingFeeRateMissingModal
        open={blockedOnFeeRate}
        onClose={() => setBlockedOnFeeRate(false)}
        onSetRate={canSetFeeRate ? () => history.push(ScreenEnum.PartnerProcessingFees) : undefined}
      />
      <h2 className="mb-6">{t('title')}</h2>
      {renderFeedbackLabel}
      <AntTabs
        activeKey={activeTab}
        onChange={(tab) => setActiveTab(tab, 'takeout_settings')}
        items={[
          {
            key: Tabs.General,
            label: t('tabs.general'),
            children: (
              <GeneralTab
                data={data}
                feedback={feedback}
                loading={loading}
                onChange={setField}
                onSave={() => save()}
                setShowConfirmLiveOrdersSwitchDrawer={setShowConfirmLiveOrdersSwitchDrawer}
                onRequestGoLive={requestGoLive}
                goLivePending={gate.pending}
              />
            ),
          },
          {
            key: Tabs.Pickup,
            label: t('tabs.pickup'),
            children: (
              <PickupTab
                data={data}
                toggleMethodVisible={() => toggleOrderMethodVisible(ORDER_METHODS.pickup)}
                validator={validator}
                feedback={feedback}
                loading={loading}
                onChange={setField}
                onSave={() => save()}
              />
            ),
          },
          {
            key: Tabs.Delivery,
            label: t('tabs.delivery'),
            children: (
              <DeliveryTab
                data={data}
                toggleMethodVisible={() => toggleOrderMethodVisible(ORDER_METHODS.delivery)}
                validator={validator}
                feedback={feedback}
                loading={loading}
                onChange={setField}
                onSave={() => save()}
              />
            ),
          },
          {
            key: Tabs.RoomService,
            label: t('tabs.room_service'),
            children: (
              <RoomServiceTab
                data={data}
                toggleMethodVisible={() => toggleOrderMethodVisible(ORDER_METHODS.room_service)}
                feedback={feedback}
                loading={loading}
                onChange={setField}
                onSave={() => save()}
              />
            ),
          },
        ].filter((item) => !hiddenTabs.includes(item.key))}
      />
      {renderConfirmLiveOrdersSwitchDrawer}
      {renderUpdateConfirmationModal}
    </>
  );
}
